/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import {
    object, shape, string, arrayOf,
} from 'prop-types';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import withStyles from '@material-ui/core/styles/withStyles';
import Grid from '@material-ui/core/Grid';
import PlaceIcon from '@material-ui/icons/Place';
import customBreakpoints from '../../../helpers/customBreakpoints';
import { getLanguageName } from '../../../../state/ducks/Member/ducks/Auth/helper/helper';
import { determineLanguagePrefixPath } from '../../../helpers/determineInternationalizationPath';

const styles = (theme) => ({
    title: {
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            width: '100%',
            maxWidth: '100%',
            marginBottom: '12px',
            flexBasis: '100%',
        },
    },
    label: {
        display: 'block',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            margin: 'auto',
        },
        '& svg': {
            position: 'absolute',
            margin: '8px',
            color: theme.palette.colorNeutral70,
        },
    },
    input: {
        width: '200',
    },
    countryBlock: {
        textAlign: 'left',
        padding: '20px',
        display: 'flex',
        position: 'relative',
        width: '800px',
        margin: '0 auto',
        bottom: '30px',
        '& p': {
            letterSpacing: '0.79px',
            margin: '0 0 0 6px',
            font: 'normal normal normal 16px/49px Roboto',
        },
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            width: '100%',
            bottom: 0,
            textAlign: 'center',
        },
    },
    countryAutocomplete: {
        width: '100%',
        textAlign: 'center',
        display: 'inline-block',
        '& input': {
            width: '100%',
            border: 'none',
            height: '40px',
            padding: '0 22px 0 35px',
        },
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            margin: 'auto',
        },
    },
    disabled: {
        pointerEvents: 'none',
        cursor: 'default',
    },
    btnContainer: {
        textAlign: 'center',
        padding: '9px',
        '& a': {
            textDecoration: 'none',
            fontSize: '15px',
            fontWeight: '600',
            lineHeight: '4px',
        },
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            width: '100%',
            margin: 'auto',
        },
    },
    fieldBlock: {
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            float: 'left',
            maxWidth: '66%',
            flexBasis: '66%',
        },
    },
    btnBlock: {
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            width: '34%',
        },
    },
    moreCountryContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '124px',
    },
    moreCountryText: {
        fontFamily: 'Roboto, Regular',
        letterSpacing: '1px',
        fontSize: '30px',
        paddingRight: '20px',
    },
    moreCountryInput: {
        width: '31%',
        textAlign: 'center',
        display: 'inline-block',
        '& input': {
            width: '100%',
            height: '72px',
            padding: '0 22px 0 60px',
            fontSize: '20px',
            border: '1px solid #919192',
            borderRight: 'none',
            borderRadius: '10px 0 0 10px',
        },
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            margin: 'auto',
        },
    },
    moreCountryButton: {
        backgroundColor: theme.palette.background.buttonGreen,
        width: '205px',
        height: '72px',
        borderRadius: '0 9px 9px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& a': {
            color: theme.palette.common.white,
            fontWeight: '700',
            fontSize: '20px',
            textDecoration: 'none',
        },
    },
    moreCountryLabel: {
        display: 'block',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            margin: 'auto',
        },
        '& svg': {
            position: 'absolute',
            margin: '25px 20px',
            color: theme.palette.colorNeutral70,
        },
    },
    errorPage: {
        bottom: 0,
    },
});

const CountryFinder = ({
    data: {
        title = '',
        placeholder = '',
        country_group = [],
        button_text = '',
        text_color = '',
        background_color = '',
        button_text_color = '',
        button_background_color = '',
        alternate_title = '',
        view_all_countries_button = '',
    },
    classes,
    ssrDeviceType = '',
    path = '',
    pageType = '',
}) => {
    const [country, setCountry] = useState('');
    const [countryPath, setCountryPath] = useState('');

    const onCountryChange = (e, option) => {
        e.preventDefault();
        const countryName = e?.target?.textContent;
        const countryURL = option?.country?.href;
        setCountry(countryName.toLowerCase());
        setCountryPath(countryURL);
    };
    const selectedLanguage = getLanguageName();
    const filterOptions = createFilterOptions({
        matchFrom: 'start',
    });
    const viewport = ssrDeviceType === 'tablet' ? 'desktop' : ssrDeviceType;

    if (path === determineLanguagePrefixPath('/morecountry') && viewport === 'desktop') {
        return (
            <div className={classes.moreCountryContainer}>
                <div className={classes.moreCountryText}>
                    {alternate_title}
                </div>
                <Autocomplete
                    className={classes.moreCountryInput}
                    id="autocomplete-input"
                    autoHighlight
                    options={country_group.sort((current, next) => ((current.country.title > next.country.title) ? 1 : -1))}
                    getOptionLabel={(option) => option.country.title}
                    filterOptions={filterOptions}
                    onChange={(e, option) => onCountryChange(e, option)}
                    renderOption={(option) => (
                        <>
                            <span style={{ margin: '5px 10px 0 0' }}>
                                <img src={option.country_flag_image.url} alt={option.title} height="25" width="25" />
                            </span>
                            {option.country.title}
                        </>
                    )}
                    renderInput={(params) => (
                        <div className={classes.moreCountryLabel} ref={params.InputProps.ref}>
                            <PlaceIcon fontSize="small" />
                            { /* eslint-disable react/jsx-props-no-spreading */}
                            <input
                                className={classes.countryInput}
                                type="text"
                                placeholder={placeholder}
                                variant="outlined"
                                label={placeholder}
                                {...params.inputProps}
                            />
                        </div>
                    )}
                />
                <div className={classes.moreCountryButton}>
                    <a href={`${selectedLanguage}${countryPath}`}>
                        {view_all_countries_button}
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className={`${classes.countryBlock} ${pageType === 'template_error_page' ? classes.errorPage : ''}`} style={{ color: text_color, backgroundColor: background_color }}>
            <Grid container alignItems="center" direction="row">
                {ssrDeviceType === 'mobile' ? null : (
                    <Grid item xs={4} className={classes.title}>
                        <p>{path === determineLanguagePrefixPath('/morecountry') ? alternate_title : title}</p>
                    </Grid>
                )}
                <Grid item md={6} xs={6} className={classes.fieldBlock}>
                    <Autocomplete
                        className={classes.countryAutocomplete}
                        id="autocomplete-input"
                        autoHighlight
                        options={country_group.sort((current, next) => ((current?.country?.title > next?.country?.title) ? 1 : -1))}
                        getOptionLabel={(option) => option?.country?.title}
                        filterOptions={filterOptions}
                        onChange={(e, option) => onCountryChange(e, option)}
                        renderOption={(option) => (
                            <>
                                <span style={{ margin: '5px 10px 0 0' }}>
                                    <img src={option?.country_flag_image?.url} alt={option.title} height="25" width="25" />
                                </span>
                                {option?.country?.title}
                            </>
                        )}
                        renderInput={(params) => (
                            <div className={classes.label} ref={params?.InputProps?.ref}>
                                <PlaceIcon fontSize="small" />
                                { /* eslint-disable react/jsx-props-no-spreading */}
                                <input
                                    className={classes.countryInput}
                                    type="text"
                                    placeholder={placeholder}
                                    variant="outlined"
                                    label={placeholder}
                                    {...params.inputProps}
                                />
                            </div>
                        )}
                    />
                </Grid>
                <Grid item md={2} className={classes.btnBlock}>
                    <div className={`${classes.btnContainer} ${(country === '' || country === null) ? classes.disabled : ''}`} style={(path === determineLanguagePrefixPath('/morecountry')) ? { backgroundColor: button_background_color, borderRadius: '0 2px 2px 0' } : { backgroundColor: button_background_color }}>
                        <a href={`${selectedLanguage}${countryPath}`} style={{ color: button_text_color }}>{ssrDeviceType === 'mobile' ? view_all_countries_button : button_text}</a>
                    </div>
                </Grid>
            </Grid>
        </div>
    );
};

CountryFinder.propTypes = {
    data: shape({
        title: string,
        placeholder: string,
        country_group: arrayOf(shape({
            country: {
                title: string,
                href: string,
            },
            country_flag_image: {
                url: string,
            },
        })),
        button_text: string,
        text_color: string,
        background_color: string,
        button_text_color: string,
        button_background_color: string,
    }).isRequired,
    classes: object.isRequired,
    ssrDeviceType: string.isRequired,
    path: string,
    pageType: string,
};

CountryFinder.defaultProps = {
    pageType: '',
    path: '',
};

export default withStyles(styles)(CountryFinder);
